'use client';

import { apiClient } from '@borradh-workspace/api-client';
import {
  type ResolvedPlaceLinkResponse,
  resolvedPlaceLinkResponseSchema,
} from '@borradh-workspace/contracts';
import { useMutation } from '@tanstack/react-query';

export type ResolvedPlaceLink = ResolvedPlaceLinkResponse;

/**
 * Shown when the lookup itself is unavailable (Google down, no key on this
 * deployment). The API masks every 5xx message, so the fallback lives here.
 */
export const PLACE_LINK_UNAVAILABLE =
  'We couldn’t look up Google Maps just now. Enter the address by hand below, or try the link again in a minute.';

const statusOf = (error: unknown): number | undefined =>
  typeof error === 'object' && error !== null && 'status' in error
    ? Number((error as { status: unknown }).status)
    : undefined;

/**
 * The owner-facing message for a failed lookup: the server's own wording for
 * a link it could read and refused (expired share link, directions link, map
 * area, not Google…), and our fallback for anything else.
 */
export const placeLinkErrorMessage = (error: unknown): string => {
  const status = statusOf(error);
  // Before the URL-aware API deploy, /places/details requires placeId and
  // replies 400 "Invalid input". Keep manual entry usable on native binaries
  // that reach users while the API rollout is still in progress.
  if (
    status === 400 &&
    error instanceof Error &&
    error.message === 'Invalid input'
  ) {
    return PLACE_LINK_UNAVAILABLE;
  }
  if (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    error instanceof Error &&
    error.message
  ) {
    return error.message;
  }
  return PLACE_LINK_UNAVAILABLE;
};

/**
 * Read a pasted Google Maps link into a name, address and pin to CONFIRM.
 * A mutation rather than a query: it runs once per click on what the owner
 * pasted, is never cached against a key, and must not retry — every retry is
 * another billed Places call for a link that already failed.
 */
export const useResolvePlaceLink = () => {
  const mutation = useMutation({
    mutationFn: (url: string) =>
      apiClient.get<ResolvedPlaceLink>('places/details', {
        searchParams: { url },
        retry: 0,
        schema: resolvedPlaceLinkResponseSchema,
      }),
  });

  return {
    resolvePlaceLink: mutation.mutate,
    resolvePlaceLinkAsync: mutation.mutateAsync,
    place: mutation.data ?? null,
    error: mutation.error ? placeLinkErrorMessage(mutation.error) : null,
    isResolving: mutation.isPending,
    reset: mutation.reset,
  };
};
